import PropTypes from "prop-types";
import { useAuth } from "hooks/useAuth";
import { useEvent } from "hooks/useEvent";
import { useTeeTime } from "hooks/useTeeTime";
import { useUserData } from "hooks/useUserData";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isTeeTimeSubmitted } from "utils/match";
import { isUserInTeeTimeUsers } from "utils/teeTime";
import { ScorecardItem } from "./components/scorecardItem";
import ScorecardLegend from "./components/scorecardLegend";

function Scorecard({ quota, teamId, teamsPlay, hideLegend, currentHoleIndex }) {
  const navigation = useNavigate();

  const { user } = useAuth();
  const { eventId, teeTimeId } = useParams();

  const userData = useUserData(user);
  const event = useEvent(eventId);
  const teeTime = useTeeTime(eventId, teeTimeId);

  const addTeamNameToEveryUserObject =
    teeTime?.teams?.map((tm) => {
      // eslint-disable-next-line no-param-reassign
      tm.users = tm.users.map((us) => ({
        ...us,
        teamName: tm.name,
      }));
      return tm;
    }) ?? [];

  const users = teamsPlay
    ? addTeamNameToEveryUserObject?.reduce(
        (acc, team) => [...acc, ...team.users],
        [],
      )
    : teeTime?.users ?? {};

  function onHoleRedirect(holeNumber) {
    navigation(`/event/${eventId}/${teeTimeId}/holes?hole=${holeNumber}`, {
      replace: true,
    });
  }

  const { teams } = teeTime ?? [];

  useEffect(() => {
    if (navigation) {
      if (event && teeTime) {
        const isJoined = isUserInTeeTimeUsers(userData, teeTime.users, teams);
        if (userData === null || (userData && !isJoined)) {
          // kick out anonymous or not joined users
          navigation(`/event/${event.id}/notJoined`);
        } else if (isTeeTimeSubmitted(event, teeTime.id)) {
          navigation(`/event/${event.id}/finalScorecard`, {
            state: { teamId },
            replace: true,
          });
        }
      }
    }
  }, [userData, event, teeTime, navigation, teams, teamId]);

  return userData && event ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <div>
        <ScorecardItem
          currentHoleIndex={currentHoleIndex}
          quota={quota}
          isSecondBetQuota={
            event?.bets.length > 0 ? event?.bets[0].matchType === 0 : false
          }
          users={users}
          onHoleClicked={(holeNumber) => onHoleRedirect(holeNumber)}
          teeTimeTime={teeTime.teeTimeString}
          skins={!!event.skins}
          ballCount={event?.ballCount}
          teamsPlay={event?.teamsPlay}
          matchType={event?.matchType}
          secondBetMatchType={
            event?.bets.length > 0 ? event?.bets[0]?.matchType : null || false
          }
        />
        {!hideLegend && <ScorecardLegend skins={event?.skins} quota={quota} />}
      </div>
    </div>
  ) : (
    <div>loading</div>
  );
}

Scorecard.propTypes = {
  quota: PropTypes.bool,
  teamId: PropTypes.string,
  teamsPlay: PropTypes.bool,
  hideLegend: PropTypes.bool,
  currentHoleIndex: PropTypes.number,
};

Scorecard.defaultProps = {
  quota: false,
  teamId: "",
  teamsPlay: false,
  hideLegend: false,
  currentHoleIndex: 0,
};

export { Scorecard };
